import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Modal, Svg, Text } from '@hopskipdrive/react-components';
import { QueryParamsForAssignRiders } from '@rideiq/organizers/types';
import { Button } from '@rideiq/shared/components/Button';
import { PageHeading } from '@rideiq/shared/components/PageHeading';
import { colorTokens } from '@rideiq/shared/components/variables';
import styled from 'styled-components';

import { useBulkReactivateRiders } from './useBulkReactivateRiders';

interface BulkReactivateModalProps {
  open: boolean;
  selectedRiderIds: number[];
  isAllFilteredSelected: boolean;
  totalCount: number;
  onClose: () => void;
  onSuccess: () => void;
  queryParams: QueryParamsForAssignRiders;
}

export const BulkReactivateModal = ({
  open,
  selectedRiderIds,
  isAllFilteredSelected,
  totalCount,
  onClose,
  onSuccess,
  queryParams,
}: BulkReactivateModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { submit, riderCount, riderLabel } = useBulkReactivateRiders({
    selectedRiderIds,
    isAllFilteredSelected,
    totalCount,
    queryParams,
    onSuccess,
  });

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await submit();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Modal.Title>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between">
          <PageHeading variant="h1" fontWeight={700}>{`Reactivate ${riderCount} ${riderLabel}`}</PageHeading>
          <CloseButton onClick={onClose}>
            <Svg height="20px" icon="close-outlined" />
          </CloseButton>
        </Box>
      </Modal.Title>
      <Modal.Content>
        <Text>
          {
            "Reactivating riders adds them to the active riders list and allows new rides to be scheduled. We recommend reviewing rider info to make sure it's up to date."
          }
        </Text>
      </Modal.Content>
      <Modal.Actions>
        <Button color="tertiary" onClick={onClose}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleConfirm} loading={isSubmitting}>
          {isSubmitting ? 'Reactivating...' : 'Confirm'}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

const CloseButton = styled(Link)`
  display: inline-flex;
  margin-left: auto;
  width: 20px;
  height: 20px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
  border: none;
  background: transparent;
  padding: 0;
  flex-shrink: 0;

  &:hover {
    background: #ddebfa;
    svg {
      color: ${colorTokens.text.hover};
    }
  }

  &:focus-visible {
    outline: 2px solid ${colorTokens.primary.main};
    outline-offset: 2px;
  }
`;
